import CelebrationIcon from '@mui/icons-material/Celebration';
import { Stack, Tooltip } from '@mui/material';
import type { FC, ReactNode } from 'react';

interface DayContentProps {
  date: Date;
  isHoliday: boolean;
  isShortDay: boolean;
  isWeekend: boolean;
  isToday: boolean;
  holidayName?: string;
  dateFormatter: Intl.DateTimeFormat;
  showTooltip?: boolean;
}

type DayType = 'holiday' | 'short-day' | 'weekend' | 'regular';

const DayTitle: FC<DayContentProps> = ({
  date,
  isHoliday,
  isShortDay,
  isWeekend,
  holidayName,
  dateFormatter,
  showTooltip = false,
}) => {
  const dayType: DayType = isHoliday
    ? 'holiday'
    : isShortDay
    ? 'short-day'
    : isWeekend
    ? 'weekend'
    : 'regular';

  const getIconByDayType = (): ReactNode => {
    switch (dayType) {
      case 'holiday':
        return <CelebrationIcon fontSize='small' />;
      default:
        return null;
    }
  };

  const getAriaLabel = (): string => {
    const dateStr = date.toLocaleString('ru', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const typeStr = isHoliday
      ? 'праздничный день'
      : isWeekend
      ? 'выходной день'
      : 'рабочий день';
    const holidayStr = holidayName ? `, ${holidayName}` : '';
    return `${dateStr}, ${typeStr}${holidayStr}`;
  };

  const content = (
    <Stack
      direction='row'
      spacing={0.5}
      alignItems='center'
      component='span'
      aria-label={getAriaLabel()}
      role='text'>
      <div>
        {dateFormatter.format(date)} {date.toLocaleString('ru', { weekday: 'short' })}
      </div>
      {getIconByDayType()}
    </Stack>
  );

  if (showTooltip && isHoliday && holidayName) {
    return (
      <Tooltip title={holidayName} placement='right'>
        {content}
      </Tooltip>
    );
  }

  return content;
};

export default DayTitle;
